import * as React from 'react';

// EasyEmail Pro SDK ----------------------------------------------------------
// Quick-start reference: https://docs.easyemail.pro/docs/tutorial/quick-start
// ---------------------------------------------------------------------------
import { EmailEditorProvider, type EmailTemplate } from 'easy-email-pro-editor';
import { EditorCore } from 'easy-email-pro-core';
import {
  Retro,
  type EditorContextProps,
  type ThemeConfigProps,
} from 'easy-email-pro-theme';

import 'easy-email-pro-theme/lib/style.css';
// The Arco theme stylesheet ships alongside the EasyEmail Pro Retro layout.
// If you customised the theme, swap this for your own compiled bundle.
import '@arco-themes/react-easy-email-pro/css/arco.css';

// ---------------------------------------------------------------------------
// TODO: Insert your EasyEmail Pro `clientId` here.
// You receive this from the EasyEmail Pro team after subscribing to a paid
// plan. In production, expose it through a Strapi admin env var prefixed
// with STRAPI_ADMIN_ so it gets injected into the admin bundle, e.g.:
//   STRAPI_ADMIN_EASYEMAIL_PRO_CLIENT_ID=ee_pro_xxxxxxxxxxxxxxxx
// ---------------------------------------------------------------------------
const CLIENT_ID: string =
  (typeof process !== 'undefined' && (process as any).env?.STRAPI_ADMIN_EASYEMAIL_PRO_CLIENT_ID) || '';

// ---------------------------------------------------------------------------
// TODO: Register Pro-only / custom blocks, plugins, or asset providers here
// before the component mounts. See the Pro docs for examples:
//   import { registerPlugin } from 'easy-email-pro-plugins';
//   import { registerAssetProvider } from 'easy-email-pro-asset-manager';
// ---------------------------------------------------------------------------

const BLANK_TEMPLATE: EmailTemplate = {
  subject: '',
  content: EditorCore.createEmailTemplate?.()?.content ?? {
    type: 'page',
    data: { value: {} },
    attributes: {},
    children: [],
  },
} as EmailTemplate;

export interface EasyEmailEditorHandle {
  /** Returns the current EmailTemplate (subject + content tree) for re-edit. */
  getValues: () => EmailTemplate | null;
  /** Compiles the current design to MJML-rendered HTML. */
  exportHtml: () => Promise<string>;
}

export interface EasyEmailEditorProps {
  /** Initial EmailTemplate. Pass `null`/`undefined` for a blank canvas. */
  initialValues?: EmailTemplate | null;
  /** Optional async asset upload handler — wire up to your Strapi upload plugin. */
  onUpload?: (file: Blob) => Promise<string>;
  /** Forwarded to the Retro layout — defaults to fill the available viewport. */
  height?: string;
}

const EasyEmailEditor = React.forwardRef<EasyEmailEditorHandle, EasyEmailEditorProps>(
  ({ initialValues, onUpload, height = 'calc(100vh - 220px)' }, ref) => {
    const instanceRef = React.useRef<EditorContextProps | null>(null);

    const startingValues = React.useMemo<EmailTemplate>(
      () => initialValues ?? BLANK_TEMPLATE,
      // Intentionally only computed once — EasyEmail's editor is uncontrolled
      // after mount; replacing initialValues mid-flight would discard edits.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

    const config = Retro.useCreateConfig({
      clientId: CLIENT_ID,
      instanceRef,
      height,
      initialValues: startingValues,
      onUpload:
        onUpload ??
        (async () => {
          // TODO: replace with your Strapi upload-plugin integration.
          throw new Error('No upload handler configured for EasyEmail editor.');
        }),
      onSubmit: async () => undefined,
    });

    React.useImperativeHandle(ref, () => ({
      getValues: () => instanceRef.current?.formState?.values ?? null,
      exportHtml: async () => {
        const values = instanceRef.current?.formState?.values as EmailTemplate | undefined;
        if (!values?.content) return '';

        const mjmlStr = EditorCore.toMJML({
          element: values.content,
          mode: 'production',
          beautify: true,
        });

        // mjml-browser is dynamically imported so the heavy compiler only
        // loads when the admin actually exports.
        const { default: mjml2html } = await import('mjml-browser');
        const { html, errors } = mjml2html(mjmlStr, { validationLevel: 'soft' });
        if (errors?.length) {
          // Soft-validation errors are usually warnings; surface to the console
          // so template authors notice unsupported MJML features.
          // eslint-disable-next-line no-console
          console.warn('[easyemail] MJML compile warnings', errors);
        }
        return html;
      },
    }));

    return (
      <EmailEditorProvider {...config}>
        <Retro.Layout />
      </EmailEditorProvider>
    );
  }
);

EasyEmailEditor.displayName = 'EasyEmailEditor';

export default EasyEmailEditor;
